import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { Checkbox, Col, Form, Input, InputNumber, Row, Select, Switch } from "antd"
import { WrappedFormUtils } from "antd/lib/form/Form"
import { zipWith } from "lodash"
import * as React from "react"
import { connect } from "react-redux"
import type { AddedToItem } from "../../../../graphql/types"
import { numberToDollarsFormatter, numberToDollarsParser } from "../../../../utils/formatters"
import { trimFormPayload } from "../../../../utils/helpers"
import { LoadingOverlayDeprecated } from "../../../common/LoadingOverlayDeprecated"
import { RequiredFormInfo } from "../../../common/RequiredFormInfo"
import { isStateLoading } from "../../../filters/selectors"
import DrawerBlockComponent from "../../../uiKit/Drawer/components/DrawerBlockComponent"
import DrawerDetailFormFooter from "../../../uiKit/Drawer/components/DrawerDetailFormFooter"
import DrawerDetailFormHeader from "../../../uiKit/Drawer/components/DrawerDetailFormHeader"
import { AddItemsToGroupTable } from "../components/AddItemsToGroupTable"
import type { AutoCompleteSelectionItem } from "../components/AssignItemAutoComplete"
import { closeModifierGroupFormClicked, deleteModifierGroupClicked, modifierGroupFormSubmitted } from "./actions"
import IndividualModifiersTable from "./IndividualModifiersTable"
import { getModificationsAutocompleteData, getModifierGroupFormData } from "./selectors"
import { DuplicateModifierGroupBtn } from "./components/DuplicateModifierGroupBtn/DuplicateModifierGroupBtn"

const MODIFIER_GROUP_FORM_ITEM_DISPLAY_TYPES = ["grid", "grid-3", "grid-4", "scale"] as const

export type ModifierGroupFormItemDisplayType = (typeof MODIFIER_GROUP_FORM_ITEM_DISPLAY_TYPES)[number]

export const isValidModifierGroupFormItemDisplayType = (value: string): value is ModifierGroupFormItemDisplayType =>
    (MODIFIER_GROUP_FORM_ITEM_DISPLAY_TYPES as readonly string[]).includes(value)

export type ModifierGroupFormItem = {
    modifierGroupId: Maybe<string>
    name: string
    displayName: Maybe<string>
    isActive: Maybe<boolean>
    isActiveOverriden?: Maybe<boolean>
    isActivePOS: Maybe<boolean>
    isActiveMIB: Maybe<boolean>
    isActiveKiosk: Maybe<boolean>
    display: ModifierGroupFormItemDisplayType
    exclusion: boolean
    isMinimumSelection: boolean
    minimumSelection: Maybe<number>
    minimumSelectionOverriden?: Maybe<boolean>
    isMaximumSelection: boolean
    maximumSelection: Maybe<number>
    maximumSelectionOverriden?: Maybe<boolean>
    isPricedAsGroup: boolean
    isPricedAsGroupOverriden?: Maybe<boolean>
    modifiersIncludedCount: Maybe<number>
    modifiersIncludedCountOverriden?: Maybe<boolean>
    modifiersIncludedForPrice: Maybe<number>
    modifiersIncludedForPriceOverriden?: Maybe<boolean>
    items: ModifierGroupModifierFormItem[]
    addedToItems: AddedToItem[]
}

export type ModifierGroupModifierFormItem = {
    groupItemId: Maybe<string>
    modifierId: string
    name: string
    price: Maybe<number>
    priceOverriden?: Maybe<boolean>
    groupExtra: Maybe<number>
    groupExtraOverriden?: Maybe<boolean>
    isActive: boolean
}
type Props = {
    formItem: Maybe<ModifierGroupFormItem>
    form: WrappedFormUtils
    modifiersAutocompleteSelection: AutoCompleteSelectionItem[]
    isLoading: boolean
    onCloseClick: () => void
    onDeleteClicked: (id: string) => void
    onSubmit: (oldValue: Maybe<ModifierGroupFormItem>, newValue: ModifierGroupFormItem) => void
    onAnyFieldChange?: () => void
}
type State = {
    displaySelected: ModifierGroupFormItemDisplayType
    minimumSelectionEnabled: boolean
    maximumSelectionEnabled: boolean
    pricedAsGroupEnabled: boolean
    addedToItems: AddedToItem[]
}

class ModifierGroupForm extends React.PureComponent<Props, State> {
    myRef = React.createRef()

    constructor(props) {
        super(props)
        this.state = {
            displaySelected: this.props.formItem ? this.props.formItem.display : "grid",
            minimumSelectionEnabled: this.props.formItem ? this.props.formItem.isMinimumSelection : false,
            maximumSelectionEnabled: this.props.formItem ? this.props.formItem.isMaximumSelection : false,
            pricedAsGroupEnabled: this.props.formItem ? this.props.formItem.isPricedAsGroup : false,
            addedToItems: this.props.formItem ? this.props.formItem.addedToItems : [],
        }
    }

    formItemLayout = {
        colon: false,
        labelCol: {
            xs: {
                span: 24,
            },
            sm: {
                span: 8,
            },
        },
        wrapperCol: {
            xs: {
                span: 24,
            },
            sm: {
                span: 16,
            },
        },
    }

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (this.props.formItem && prevProps.formItem !== this.props.formItem) {
            this.setState({
                displaySelected: this.props.formItem ? this.props.formItem.display : "grid",
                minimumSelectionEnabled: this.props.formItem ? this.props.formItem.isMinimumSelection : false,
                maximumSelectionEnabled: this.props.formItem ? this.props.formItem.isMaximumSelection : false,
                pricedAsGroupEnabled: this.props.formItem ? this.props.formItem.isPricedAsGroup : false,
                addedToItems: this.props.formItem ? this.props.formItem.addedToItems : [],
            })
        }
    }

    onCloseClick = () => {
        this.props.onCloseClick()
    }
    onDeleteClick = () => {
        if (this.props.formItem && this.props.formItem.modifierGroupId) {
            this.props.onDeleteClicked(this.props.formItem.modifierGroupId)
        }
    }
    onActiveSwitchChange = e => {
        return e
    }
    onMinimumSelectionClicked = val => {
        this.props.form.setFieldsValue({
            isMinimumSelection: val.target.checked,
        })
        this.setState({
            minimumSelectionEnabled: val.target.checked,
        })
        this.props.onAnyFieldChange?.()
    }
    onMaximumSelectionClicked = val => {
        this.props.form.setFieldsValue({
            isMaximumSelection: val.target.checked,
        })
        this.setState({
            maximumSelectionEnabled: !this.state.maximumSelectionEnabled,
        })
        this.props.onAnyFieldChange?.()
    }
    onPricedAsGroupClicked = e => {
        this.setState({
            pricedAsGroupEnabled: e.target.checked,
        })
        this.props.onAnyFieldChange?.()
    }
    handleDisplaySelect = value => {
        if (value === "scale" && this.props.formItem) {
            this.props.formItem.isPricedAsGroup = false
        }

        this.setState({
            displaySelected: value,
            pricedAsGroupEnabled: value === "scale" ? false : this.state.pricedAsGroupEnabled,
        })
        this.props.onAnyFieldChange?.()
    }

    handleSubmit = () => {
        this.props.form.validateFields((err, values) => {
            const price = values["individual-modifier-price"]
            const names = values["individual-modifier-name"]
            const groupPrice = values["individual-modifier-group-price"]
            const optionItems2 = price
                ? zipWith(
                      values["individual-modifier-id"],
                      values["individual-modifier-group-item-id"],
                      names,
                      (modifierId: string, groupItemId, modifierName) => ({
                          modifierId: modifierId || null,
                          groupItemId: groupItemId || null,
                          name: modifierName,
                          price: price[modifierId] ? parseFloat(price[modifierId]) : 0,
                          groupExtra: groupPrice[modifierId] ? parseFloat(groupPrice[modifierId]) : 0,
                      })
                  )
                : []
            // Handle Location Overrides
            const enrichedItems = this.props.formItem
                ? optionItems2.map(op => {
                      // @ts-expect-error
                      const find = this.props.formItem.items.find(item => item.modifierId === op.modifierId)
                      return find ? { ...find, ...op } : op
                  })
                : optionItems2
            const valuesProcessed = trimFormPayload(values)

            if (!err) {
                this.props.onSubmit(this.props.formItem, {
                    ...this.props.formItem,
                    modifierGroupId: this.props.formItem?.modifierGroupId ?? null,
                    name: valuesProcessed.name,
                    displayName: valuesProcessed.displayName,
                    isActive: valuesProcessed.isActive,
                    isActivePOS: valuesProcessed.isActivePOS,
                    isActiveMIB: valuesProcessed.isActiveMIB,
                    isActiveKiosk: valuesProcessed.isActiveKiosk,
                    display: valuesProcessed.display,
                    exclusion: valuesProcessed.exclusion,
                    isMinimumSelection: this.state.minimumSelectionEnabled,
                    minimumSelection: valuesProcessed.minimumSelection,
                    isMaximumSelection: this.state.maximumSelectionEnabled,
                    maximumSelection: valuesProcessed.maximumSelection,
                    isPricedAsGroup: this.state.pricedAsGroupEnabled,
                    modifiersIncludedCount: valuesProcessed.modifiersIncludedCount,
                    modifiersIncludedForPrice:
                        valuesProcessed.modifiersIncludedForPrice || valuesProcessed.modifiersIncludedForPrice === 0
                            ? valuesProcessed.modifiersIncludedForPrice
                            : null,
                    items: enrichedItems,
                    addedToItems: this.state.addedToItems,
                } as ModifierGroupFormItem)
            }
        })
    }
    renderNameInput = () => {
        const options = {
            rules: [
                {
                    max: 50,
                    required: true,
                },
            ],
            initialValue: this.props.formItem ? this.props.formItem.name : null,
        }
        return (
            <Form.Item
                {...this.formItemLayout}
                style={{
                    marginBottom: "1rem",
                }}
                label="Group Name">
                <Row>
                    <Col span={22}>
                        {this.props.form.getFieldDecorator(
                            "name",
                            options
                        )(<Input autoFocus={!this.props.formItem} onFocus={e => e.target.select()} />)}
                    </Col>
                </Row>
            </Form.Item>
        )
    }
    renderDisplayNameInput = () => {
        const options = {
            rules: [
                {
                    max: 50,
                },
            ],
            initialValue: this.props.formItem ? this.props.formItem.displayName : null,
        }
        return (
            <Form.Item
                {...this.formItemLayout}
                style={{
                    marginBottom: "1rem",
                }}
                label="Display Name">
                <Row>
                    <Col span={22}>
                        {this.props.form.getFieldDecorator(
                            "displayName",
                            options
                        )(<Input onFocus={e => e.target.select()} />)}
                    </Col>
                </Row>
            </Form.Item>
        )
    }
    renderDisplay = () => {
        const options = {
            initialValue: this.state.displaySelected,
        }
        return (
            <div
                // @ts-expect-error
                ref={this.myRef}
                style={{
                    position: "relative",
                    zIndex: 10,
                }}>
                <Form.Item
                    style={{
                        marginBottom: "1rem",
                    }}
                    {...this.formItemLayout}
                    label="Display">
                    <Row>
                        <Col span={10}>
                            {this.props.form.getFieldDecorator(
                                "display",
                                options
                            )(
                                // @ts-iignore
                                <Select
                                    onChange={this.handleDisplaySelect}
                                    className="fill"
                                    // @ts-expect-error
                                    getPopupContainer={() => this.myRef.current}>
                                    <Select.Option value="scale">Scale (Horizontal)</Select.Option>
                                    <Select.Option value="grid">Grid (2 columns)</Select.Option>
                                    <Select.Option value="grid-3">Grid (3 columns)</Select.Option>
                                    <Select.Option value="grid-4">Grid (4 columns)</Select.Option>
                                </Select>
                            )}
                        </Col>
                    </Row>
                </Form.Item>
            </div>
        )
    }
    renderActiveSwitch = () => {
        const options = {
            initialValue: this.props.formItem ? this.props.formItem.isActive : true,
        }
        return (
            <Form.Item
                {...this.formItemLayout}
                style={{
                    marginBottom: "1rem",
                }}
                label="Active">
                <Row>
                    <Col span={24}>
                        {this.props.form.getFieldDecorator(
                            "isActive",
                            options
                        )(
                            <Switch
                                size="small"
                                // @ts-expect-error
                                defaultChecked={this.props.formItem ? this.props.formItem.isActive : true}
                                onChange={this.onActiveSwitchChange}
                            />
                        )}
                    </Col>
                </Row>
            </Form.Item>
        )
    }
    renderExclusion = () => {
        const options = {
            initialValue: this.props.formItem ? this.props.formItem.exclusion : false,
        }
        return (
            <Form.Item
                {...this.formItemLayout}
                style={{
                    marginBottom: "1rem",
                }}
                label="Exclusion">
                <Row>
                    <Col span={24}>
                        {this.props.form.getFieldDecorator(
                            "exclusion",
                            options
                        )(
                            <Checkbox
                                disabled={this.state.displaySelected === "scale"}
                                defaultChecked={this.props.formItem ? this.props.formItem.exclusion : false}
                            />
                        )}
                    </Col>
                </Row>
            </Form.Item>
        )
    }
    renderActiveOnSource = (label: string, key: string) => {
        const options = {
            initialValue: this.props.formItem ? this.props.formItem[key] : true,
            valuePropName: "checked",
        }
        return (
            <Form.Item
                {...this.formItemLayout}
                style={{
                    marginBottom: "0.5rem",
                }}
                label={<span />}>
                <Row>
                    <Col span={24}>
                        {this.props.form.getFieldDecorator(
                            `${key}`,
                            options
                        )(<Checkbox defaultChecked={this.props.formItem ? this.props.formItem[key] : true} />)}
                        <span
                            style={{
                                marginLeft: "1rem",
                            }}>
                            Active On <b>{label}</b>
                        </span>
                    </Col>
                </Row>
            </Form.Item>
        )
    }
    renderMinimumSelection = () => {
        const options = {
            initialValue: this.props.formItem ? this.props.formItem.isMinimumSelection : false,
        }
        const options2 = {
            initialValue: this.props.formItem ? this.props.formItem.minimumSelection : 0,
        }
        return (
            <Form.Item
                {...this.formItemLayout}
                style={{
                    marginBottom: "1rem",
                }}
                label="Minimum Selection">
                <Row>
                    <Col span={22}>
                        {this.props.form.getFieldDecorator(
                            "isMinimumSelection",
                            options
                        )(
                            <Checkbox
                                checked={this.props.form.getFieldValue("isMinimumSelection")}
                                onChange={this.onMinimumSelectionClicked}
                            />
                        )}

                        <div
                            style={{
                                display: "inline-block",
                                marginLeft: "1.5rem",
                            }}>
                            {this.props.form.getFieldDecorator(
                                "minimumSelection",
                                options2
                            )(
                                <Input
                                    disabled={!this.state.minimumSelectionEnabled}
                                    style={{
                                        width: "3rem",
                                    }}
                                />
                            )}
                            <span
                                style={{
                                    marginLeft: "1rem",
                                }}>
                                {" "}
                                items{" "}
                            </span>
                        </div>
                    </Col>
                </Row>
            </Form.Item>
        )
    }
    renderMaximumSelection = () => {
        const options = {
            initialValue: this.props.formItem ? this.props.formItem.isMaximumSelection : false,
        }
        const options2 = {
            initialValue: this.props.formItem ? this.props.formItem.maximumSelection : 0,
        }
        return (
            <Form.Item
                {...this.formItemLayout}
                style={{
                    marginBottom: "1rem",
                }}
                label="Maximum Selection">
                <Row>
                    <Col span={22}>
                        {this.props.form.getFieldDecorator(
                            "isMaximumSelection",
                            options
                        )(
                            <Checkbox
                                checked={this.props.form.getFieldValue("isMaximumSelection")}
                                onChange={this.onMaximumSelectionClicked}
                            />
                        )}

                        <div
                            style={{
                                display: "inline-block",
                                marginLeft: "1.5rem",
                            }}>
                            {this.props.form.getFieldDecorator(
                                "maximumSelection",
                                options2
                            )(
                                <Input
                                    onFocus={e => e.target.select()}
                                    disabled={!this.state.maximumSelectionEnabled}
                                    style={{
                                        width: "3rem",
                                    }}
                                />
                            )}
                            <span
                                style={{
                                    marginLeft: "1rem",
                                }}>
                                {" "}
                                items{" "}
                            </span>
                        </div>
                    </Col>
                </Row>
            </Form.Item>
        )
    }

    renderPricedAsGroup = () => {
        const options = {
            initialValue: this.props.formItem ? this.props.formItem.isPricedAsGroup : false,
        }
        const options2 = {
            initialValue: this.props.formItem ? this.props.formItem.modifiersIncludedCount : 0,
        }
        const options3 = {
            initialValue: this.props.formItem ? this.props.formItem.modifiersIncludedForPrice : 0,
        }
        return (
            <Form.Item
                {...this.formItemLayout}
                style={{
                    marginBottom: "1rem",
                }}
                label="Priced as a Group">
                <Row>
                    <Col span={24}>
                        {this.props.form.getFieldDecorator(
                            "isPricedAsGroup",
                            options
                        )(
                            <Checkbox
                                disabled={this.state.displaySelected === "scale"}
                                checked={this.state.pricedAsGroupEnabled}
                                onChange={e => this.onPricedAsGroupClicked(e)}
                            />
                        )}

                        <div
                            style={{
                                display: "inline-block",
                                marginLeft: "1.5rem",
                            }}>
                            {this.props.form.getFieldDecorator(
                                "modifiersIncludedCount",
                                options2
                            )(
                                <Input
                                    onFocus={e => e.target.select()}
                                    disabled={!this.state.pricedAsGroupEnabled}
                                    // @ts-expect-error
                                    defaultValue={this.props.formItem ? this.props.formItem.modifiersIncludedCount : 0}
                                    style={{
                                        width: "3rem",
                                    }}
                                />
                            )}
                            <span
                                style={{
                                    marginLeft: "1rem",
                                }}>
                                {" "}
                                modifiers included for{" "}
                            </span>
                        </div>

                        <div
                            style={{
                                display: "inline-block",
                                marginLeft: "0.5rem",
                            }}>
                            {this.props.form.getFieldDecorator(
                                "modifiersIncludedForPrice",
                                options3
                            )(
                                <InputNumber
                                    onFocus={e => e.target.select()}
                                    key="priced-as-group-price"
                                    step={0.1}
                                    formatter={numberToDollarsFormatter}
                                    parser={numberToDollarsParser}
                                    // @ts-expect-error
                                    defaultValue={
                                        this.props.formItem ? this.props.formItem.modifiersIncludedForPrice : 9
                                    }
                                    disabled={!this.state.pricedAsGroupEnabled}
                                    style={{
                                        width: "4.5rem",
                                        marginLeft: "0.5rem",
                                    }}
                                />
                            )}
                        </div>
                    </Col>
                </Row>
            </Form.Item>
        )
    }
    renderIndividualOptionsTable = () => {
        const exclusion =
            this.props.form.getFieldValue("exclusion") != null
                ? this.props.form.getFieldValue("exclusion")
                : this.props.formItem
                  ? this.props.formItem.exclusion
                  : false

        return (
            <React.Fragment>
                <IndividualModifiersTable
                    isNew={!this.props.formItem}
                    exclusion={exclusion}
                    displaySelected={this.state.displaySelected}
                    modifiersAutocompleteSelection={this.props.modifiersAutocompleteSelection}
                    form={this.props.form}
                    data={this.props.formItem && this.props.formItem.items ? this.props.formItem.items : []}
                    onAnyFieldChange={this.props.onAnyFieldChange}
                />
            </React.Fragment>
        )
    }

    onItemsChanged = (newItems: AddedToItem[]) => {
        this.setState({
            addedToItems: newItems,
        })
        this.props.onAnyFieldChange?.()
    }

    renderItemsList = () => (
        <div>
            <AddItemsToGroupTable addedToItems={this.state.addedToItems} onItemsChanged={this.onItemsChanged} />
        </div>
    )

    render() {
        return (
            <div>
                <style jsx={true} global={true}>
                    {`
                        .category-item-modal .ant-form-item-label {
                            padding-right: 0.5rem;
                        }
                        .category-item-modal .ant-dropdown {
                            width: 100%;
                        }
                        .category-item-modal .individual-modifier-list-container {
                            display: flex;
                            justify-content: center;
                            align-content: center;
                            align-items: center;
                            webkit-flex-flow: column wrap;
                            flex-flow: column wrap;
                        }
                        .category-item-modal .div-item {
                            width: 267px;
                            padding: 1rem;
                            background-color: white;
                            border: 1px solid #d9d9d9;
                            margin: 9px;
                            border-radius: 4px;
                            line-height: 2;
                        }

                        .assigned-items-dropdown .ant-dropdown {
                            width: 100%;
                        }
                    `}
                </style>
                {this.props.isLoading && <LoadingOverlayDeprecated />}
                <DrawerDetailFormHeader
                    title="Modifier Group"
                    isNew={!this.props.formItem}
                    onCancelClicked={() => this.onCloseClick()}
                />

                <DrawerDetailFormFooter
                    onCancelClicked={this.onCloseClick}
                    onSubmitClicked={this.handleSubmit}
                    onDeleteClicked={this.onDeleteClick}
                    deleteConfirmMessage="Do you really want to delete this modifier group?"
                    showDeleteButton={!!this.props.formItem}
                    additionalButtons={
                        this.props.formItem ? (
                            <div className="-ml-4 inline-block">
                                <DuplicateModifierGroupBtn formItem={this.props.formItem} />
                            </div>
                        ) : undefined
                    }
                />

                <Form onSubmit={this.handleSubmit}>
                    <div
                        className="category-item-modal"
                        style={{
                            padding: 24,
                            paddingTop: 36,
                        }}>
                        <DrawerBlockComponent title="General">
                            {this.renderNameInput()}
                            {this.renderDisplayNameInput()}
                            {this.renderActiveSwitch()}
                            {this.renderDisplay()}
                            {this.renderExclusion()}
                            {this.renderMinimumSelection()}
                            {this.renderMaximumSelection()}
                            {this.renderPricedAsGroup()}
                            {this.renderActiveOnSource("POS", "isActivePOS")}
                            {this.renderActiveOnSource("Online Ordering", "isActiveMIB")}
                            {this.renderActiveOnSource("Kiosk", "isActiveKiosk")}
                        </DrawerBlockComponent>
                        {this.renderIndividualOptionsTable()}
                        {this.renderItemsList()}
                        <div>
                            <RequiredFormInfo />
                        </div>
                    </div>
                </Form>
            </div>
        )
    }
}

const mapStateToProps = state => ({
    isLoading: isStateLoading(state, "modifierGroups") || isStateLoading(state, "itemsAndCategories"),
    formItem: getModifierGroupFormData(state),
    modifiersAutocompleteSelection: getModificationsAutocompleteData(state),
})

const mapDispatchToProps = {
    onSubmit: modifierGroupFormSubmitted,
    onCloseClick: closeModifierGroupFormClicked,
    onDeleteClicked: deleteModifierGroupClicked,
}
export default connect<any, any, any>(
    mapStateToProps,
    mapDispatchToProps
)(
    Form.create({
        onValuesChange: (props: Props) => props.onAnyFieldChange?.(),
    })(ModifierGroupForm)
)
